import React, { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Card,
  CardContent,
  Divider,
} from '@mui/material'
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AutoAwesome as AutoIcon,
  PhoneIphone as PhoneIcon,
} from '@mui/icons-material'
import integrationService from '../../services/integrationService'
import { getErrorMessage } from '../../utils/errorHandlers'

interface SMSTemplateOption {
  id: string
  name: string
  code: string
  content?: string
  sms_message?: string
  dlt_template_id?: string
  template_config?: Record<string, any>
  variables?: string[]
}

interface Props {
  initialTemplateId?: string
}

const SMSTestMessageTab: React.FC<Props> = ({ initialTemplateId }) => {
  const [templates, setTemplates] = useState<SMSTemplateOption[]>([])
  const [loading, setLoading] = useState(true)
  const [templateId, setTemplateId] = useState(initialTemplateId || '')
  const [phone, setPhone] = useState('')
  const [sampleDataText, setSampleDataText] = useState('{}')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<{ success: boolean; message: string; details?: any } | null>(null)

  useEffect(() => {
    integrationService
      .getTemplates({ channel: 'sms' })
      .then((data) => {
        const list = Array.isArray(data) ? data : (data as any)?.items || []
        setTemplates(list as SMSTemplateOption[])
        if (!templateId && list.length > 0) {
          setTemplateId(list[0].id)
        }
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (initialTemplateId) {
      setTemplateId(initialTemplateId)
    }
  }, [initialTemplateId])

  const selectedTemplate = templates.find((t) => t.id === templateId)
  const dltId = selectedTemplate?.dlt_template_id || selectedTemplate?.template_config?.dlt_template_id
  const templateBody = selectedTemplate?.content || selectedTemplate?.sms_message || ''

  // Auto populate sample data based on selected template
  const handleAutoFillVariables = () => {
    if (!selectedTemplate) return
    const sample: Record<string, any> = {}
    const vars = selectedTemplate.variables || []

    vars.forEach((v) => {
      if (v === 'customer_name') sample[v] = 'Rahul Sharma'
      else if (v === 'order_id') sample[v] = 'ORD-2026-9842'
      else if (v === 'amount') sample[v] = '4,500'
      else if (v === 'otp_code') sample[v] = '582914'
      else if (v === 'company_name') sample[v] = 'Dhanshika Enterprises'
      else if (v === 'tracking_url') sample[v] = 'https://dhanshika.in/track/TRK123'
      else if (v === 'checkout_url') sample[v] = 'https://dhanshika.in/cart'
      else if (v === 'payment_id') sample[v] = 'PAY-98124'
      else sample[v] = `Sample ${v}`
    })

    setSampleDataText(JSON.stringify(sample, null, 2))
  }

  // Live preview text with variable replacement
  let previewText = templateBody
  try {
    const parsedData = JSON.parse(sampleDataText || '{}')
    Object.keys(parsedData).forEach((k) => {
      const reg = new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g')
      previewText = previewText.replace(reg, String(parsedData[k]))
    })
  } catch {
    // Keep raw if JSON is temporarily invalid while typing
  }

  const isUnicode = /[^\u0000-\u007F]/.test(previewText)
  const previewCharLimit = isUnicode ? 70 : 160
  const previewCredits = previewText.length === 0 ? 0 : Math.ceil(previewText.length / previewCharLimit)

  const handleSend = async () => {
    if (!templateId || !phone.trim()) {
      setResult({ success: false, message: 'Please select a template and enter a valid recipient mobile number.' })
      return
    }

    setSending(true)
    setResult(null)

    let sampleData: Record<string, any> = {}
    try {
      sampleData = JSON.parse(sampleDataText || '{}')
    } catch {
      setResult({ success: false, message: 'Sample data must be a valid JSON object.' })
      setSending(false)
      return
    }

    try {
      const response = await integrationService.testTemplate(templateId, phone.trim(), sampleData)
      const resAny = response as any
      setResult({
        success: resAny.success !== false && resAny.status !== 'failed',
        message: resAny.message || 'SMS dispatched successfully via configured SMS Gateway!',
        details: resAny.data || resAny,
      })
    } catch (err: any) {
      setResult({
        success: false,
        message: getErrorMessage(err, 'Failed to send test SMS message'),
      })
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box maxWidth={900}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '1.05rem', mb: 0.5 }}>
          Send a Real Test SMS
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
          Pick an SMS template, specify a recipient mobile number, and dispatch an actual SMS through TrueBulkSMS
          to verify template syntax, variable rendering, and telecom DLT ID delivery.
        </Typography>

        <Stack spacing={3}>
          {/* Template Selection */}
          <Box>
            <TextField
              select
              label="Select SMS Template"
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value)
                setResult(null)
              }}
              fullWidth
            >
              {templates.length === 0 && (
                <MenuItem disabled value="">
                  No SMS templates found
                </MenuItem>
              )}
              {templates.map((t) => {
                const hasDlt = Boolean(t.dlt_template_id || t.template_config?.dlt_template_id)
                return (
                  <MenuItem key={t.id} value={t.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span>
                        {t.name} ({t.code})
                      </span>
                      <Chip
                        label={hasDlt ? 'DLT Registered' : 'Missing DLT ID'}
                        size="small"
                        sx={{
                          ml: 2,
                          height: 20,
                          fontSize: '0.7rem',
                          bgcolor: hasDlt ? '#F0FDF4' : '#FFFBEB',
                          color: hasDlt ? '#166534' : '#92400E',
                        }}
                      />
                    </Box>
                  </MenuItem>
                )
              })}
            </TextField>

            {selectedTemplate && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  DLT Template ID:
                </Typography>
                {dltId ? (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: '13px !important', color: '#16A34A !important' }} />}
                    label={dltId}
                    size="small"
                    sx={{ height: 22, fontSize: '0.75rem', fontFamily: 'monospace', bgcolor: '#F0FDF4', color: '#166534' }}
                  />
                ) : (
                  <Chip
                    icon={<WarningIcon sx={{ fontSize: '13px !important', color: '#D97706 !important' }} />}
                    label="No DLT ID configured — Carrier may reject delivery in India"
                    size="small"
                    sx={{ height: 22, fontSize: '0.75rem', bgcolor: '#FFFBEB', color: '#92400E' }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Recipient Phone */}
          <TextField
            label="Recipient Mobile Number"
            placeholder="e.g. 9876543210 or 919876543210"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            required
            helperText="10-digit Indian mobile number to receive the test SMS"
            InputProps={{
              startAdornment: <PhoneIcon sx={{ color: '#94A3B8', fontSize: 20, mr: 1 }} />,
            }}
          />

          {/* Sample Variables Data */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                Sample Variables (JSON)
              </Typography>
              <Button
                size="small"
                startIcon={<AutoIcon sx={{ fontSize: 14 }} />}
                onClick={handleAutoFillVariables}
                disabled={!selectedTemplate}
                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
              >
                Auto-fill Sample Values
              </Button>
            </Stack>
            <TextField
              value={sampleDataText}
              onChange={(e) => setSampleDataText(e.target.value)}
              multiline
              rows={4}
              fullWidth
              sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
          </Box>

          {/* Live Mobile Message Preview Card */}
          <Card variant="outlined" sx={{ bgcolor: '#F8FAFC', borderRadius: 2.5, borderColor: '#E2E8F0' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                  Rendered SMS Preview
                </Typography>
                <Chip
                  label={`${previewText.length} chars • ${previewCredits} SMS ${isUnicode ? '(Unicode)' : '(GSM-7)'}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: isUnicode ? '#FEF3C7' : '#EFF6FF',
                    color: isUnicode ? '#92400E' : '#1E40AF',
                  }}
                />
              </Stack>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: '#FFFFFF',
                  borderRadius: 2,
                  border: '1px solid #E2E8F0',
                  color: '#0F172A',
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {previewText || <span style={{ color: '#94A3B8' }}>Select a template to view rendered text...</span>}
              </Paper>
            </CardContent>
          </Card>

          {/* Send Button */}
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              onClick={handleSend}
              disabled={sending || !templateId || !phone.trim()}
              sx={{
                bgcolor: '#0C2340',
                px: 3,
                py: 1.2,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { bgcolor: '#1E293B' },
              }}
            >
              {sending ? 'Dispatching SMS...' : 'Send Real Test SMS'}
            </Button>
          </Box>

          {/* Results Display */}
          {result && (
            <Alert
              severity={result.success ? 'success' : 'error'}
              sx={{ borderRadius: 2 }}
              onClose={() => setResult(null)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {result.success ? 'Dispatch Successful' : 'Dispatch Failed'}
              </Typography>
              <Typography variant="body2">{result.message}</Typography>
              {result.details && (
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: 'rgba(0,0,0,0.04)',
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflowX: 'auto',
                  }}
                >
                  {JSON.stringify(result.details, null, 2)}
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </Paper>
    </Box>
  )
}

export default SMSTestMessageTab
